import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AddressValidationResponse, PatientAddress } from 'src/app/models/patient.model';
import { AddressAutocompleteService } from 'src/app/services/address-autocomplete/address-autocomplete.service';
import { AddressUSPSValidatorService } from 'src/app/services/address-validator/address-uspsvalidator.service';
import { DataService } from 'src/app/services/data.service';
import { AddressSuggestion } from 'src/app/services/models/address.model';
import { SnackService } from 'src/app/services/snack.service';

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
})
export class AddressComponent implements OnInit, OnChanges {
  @Input()
  readOnly = false;
  @Input()
  address: PatientAddress;
  @Input()
  expandPanel = false;
  @Input()
  saveLabel = 'Save';
  @Output()
  toSaveAddress = new EventEmitter<PatientAddress>();
  @Output()
  toDeleteAddress = new EventEmitter<PatientAddress>();
  addressForm: UntypedFormGroup;
  states: {
    name: string;
    abbreviation: string;
  }[] = [];
  loading = false;
  correctionMessage = '';
  addressValidated$: Observable<boolean>;
  filteredAddresses$: Observable<AddressSuggestion[]>;
  isSearching = false;
  results: AddressSuggestion[] = [];
  destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private dataService: DataService,
    private snackService: SnackService,
    private addressValidator: AddressUSPSValidatorService,
    private addressAutocomplete: AddressAutocompleteService
  ) {
    this.addressForm = this.fb.group({
      address: ['', Validators.required],
      address2: [''],
      state: ['', Validators.required],
      postalCode: ['', Validators.required],
      city: ['', Validators.required],
      county: ['', Validators.required],
      primary: [false],
    });
  }

  ngOnInit(): void {
    this.states = this.dataService.statesWithabbreviation;
    this.initializeForm();
    this.setupAddressAutocomplete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.address && changes.address.currentValue) {
      this.initializeForm();
    }
  }

  initializeForm(): void {
    if (this.address) {
      this.addressForm.patchValue(this.address);
      if (this.addressForm.get('postalCode').value === '') {
        this.addressForm.get('postalCode').setValue(this.address.zip);
      }
    }
  }

  saveAddress(): void {
    this.address = {
      ...this.addressForm.getRawValue(),
      id: this.address?.doc?.id ?? undefined,
    };
    this.toSaveAddress.emit(this.address);
  }

  deleteAddress(): void {
    this.address = {
      ...this.addressForm.getRawValue(),
      id: this.address?.doc?.id ?? undefined,
    };
    this.toDeleteAddress.emit(this.address);
  }

  onValidateAddress(): void {
    this.loading = true;
    this.addressValidated$ = of(false);
    this.addressValidator.validateAndApply(this.addressForm).pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.correctionMessage = result.correctionMessage;
      this.addressValidated$ = of(result.valid);
      this.loading = false;
    });
  }

  setupAddressAutocomplete(): void {
    const addressControl = this.addressForm.get('address');

    this.filteredAddresses$ = addressControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((value: string) => {
        this.isSearching = true;

        return this.addressAutocomplete.searchAddress(value).pipe(
          tap(() => {
            this.isSearching = false;
          }),
          catchError((error) => {
            console.error('Error in component during search:', error);
            this.isSearching = false;
            return of([]);
          })
        );
      })
    );

    this.filteredAddresses$.pipe(takeUntil(this.destroy$)).subscribe(
      (results) => {
        this.results = results;
      },
      (error) => console.error('Error in component subscription:', error)
    );
  }

  selectAddress(suggestion: AddressSuggestion): void {
    if (suggestion) {
      const addressLine =
        suggestion.addressLabel ||
        suggestion.formattedAddress ||
        suggestion.text ||
        suggestion.description ||
        suggestion.address ||
        '';

      this.addressForm.patchValue({
        address: addressLine,
        city: suggestion.city || this.addressForm.get('city').value,
        county: suggestion.county || this.addressForm.get('county').value,
        state: suggestion.stateCode || this.addressForm.get('state').value,
        postalCode: suggestion.postalCode || this.addressForm.get('postalCode').value,
      });
    }
  }

}
